import React, {useEffect, useMemo, useState} from "react";
import {Prompt} from "react-router-dom";
import fileDownload from "js-file-download";
import {Button, CircularProgress, Divider, IconButton} from "@mui/material";
import {
    Delete as DeleteIcon,
    Download as DownloadIcon,
    NavigateBefore as PrevIcon,
    NavigateNext as NextIcon
} from "@mui/icons-material";
import {Cards, ConfirmDeleteDialog, FloatingAddButton, UserForm, VerticalDivider} from "@atttomyx/react-components";
import {FileCard} from "../cards";
import {AddFileDialog} from "../dialogs";
import {UserTypeForm} from "../forms";
import {usePaging} from "@atttomyx/shared-hooks";
import {documentService, userService} from "../../services";
import {arrays, forms, objects, strings} from "@atttomyx/shared-utils";
import {userUtils} from "../../utils";
import {verbiage} from "@atttomyx/shared-constants";
import {RANK_GOLD, TYPE_INVENTOR} from "../../constants";
import "./editUserPage.css";

const isValid = (user) => strings.isNotBlank(user.id);

const toData = (user) => {
    return {
        imageUrl: user.imageUrl,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        phone: user.phone,
        valid: isValid(user),
    }
};

const toPermissions = (user) => {
    const settings = objects.defaultIfNullOrUndefined(user.settings, {});
    const type = settings.type;

    return {
        type: type,
        rank: objects.defaultIfNullOrUndefined(settings.rank, RANK_GOLD),
        valid: strings.isNotBlank(type),
    }
};

const toSettings = (user) => {
    const data = user.settings ? objects.deepCopy(user.settings) : {};

    data.valid = isValid(user);

    return data;
};

const EditUserPage = (props) => {
    const { snackbar, dimensions, match, user : me, users, documents, filters,
        onSaveUser, onSaveDocument, onDeleteDocument, onPrev, onNext } = props;
    const [ user, setUser ] = useState({})
    const [ data, setData ] = useState(toData({}));
    const [ permissions, setPermissions ] = useState(toPermissions({}));
    const [ settings, setSettings ] = useState(toSettings({}));
    const [ saving, setSaving ] = useState(false);
    const [ file, setFile ] = useState(null);
    const [ downloading, setDownloading ] = useState(null);
    const [ showAdd, setShowAdd ] = useState(false);
    const [ showDelete, setShowDelete ] = useState(false);
    const [ prevId, setPrevId ] = useState(null);
    const [ nextId, setNextId ] = useState(null);
    const paging = usePaging();

    const showFiles = useMemo(() => {
        return user && user.id && user.settings && user.settings.type === TYPE_INVENTOR;
    }, [user]);

    const files = useMemo(() => {
        const array = [];

        if (showFiles) {
            const ownedByUser = documents.filter(document => document.userId === user.id);

            arrays.addAll(array, ownedByUser);
        }

        return array;
    }, [user, documents, showFiles]);

    const loadUser = (userId) => {
        const filtered = filters.filter(users);
        const user = arrays.findEntity(filtered, userId) || {};
        const ids = arrays.getPrevNextIds(filtered, user, me.id);

        setUser(user);
        setPrevId(ids.prevId);
        setNextId(ids.nextId);
    };

    const redirectToPrev = () => {
        onPrev(prevId);
        loadUser(prevId);
    };

    const redirectToNext = () => {
        onNext(nextId);
        loadUser(nextId);
    };

    useEffect(() => {
        const userId = match.params.id;

        loadUser(userId);
    }, []);

    useEffect(() => {
        setData(toData(user));
        setPermissions(toPermissions(user));
        setSettings(toSettings(user));
    }, [user]);

    const resetForm = () => {
        setData(toData(user));
        setPermissions(toPermissions(user));
        setSettings(toSettings(user));
    };

    const cleanForm = () => {
        setData(forms.cleanData(data));
        setPermissions(forms.cleanData(permissions));
        setSettings(forms.cleanData(settings));
    };

    const submitForm = () => {
        setSaving(true);

        const type = permissions.type;
        const rank = permissions.rank;
        const modified = {
            imageUrl: data.imageUrl,
            firstName: data.firstName,
            lastName: data.lastName,
            alias: user.alias,
            email: data.email,
            phone: data.phone,
            birthday: data.birthday,
            roles: userUtils.getRoles(type),
            settings: userUtils.getSettings(forms.rawData(settings), type, rank),
        };

        const success = (user) => {
            cleanForm();
            setSaving(false);

            snackbar.setSuccess("User saved");
            setUser(user);
            onSaveUser(user);
        };

        const failure = (err) => {
            setSaving(false);

            snackbar.setError(err);
        };

        userService.saveUser(user.id, modified, success, failure);
    };

    const downloadFile = (document) => {
        setDownloading(document);

        const success = (bytes) => {
            fileDownload(bytes, document.fileName);
            setDownloading(null);
        };

        const failure = (err) => {
            snackbar.setError(err);
            setDownloading(null);
        };

        documentService.downloadFile(document.id, success, failure);
    };

    const valid = data.valid && permissions.valid && settings.valid;
    const modified = forms.differ(toData(user), data) || forms.differ(toPermissions(user), permissions)
        || forms.differ(toSettings(user), settings);

    return saving ?
        <div className="edit-user-page">
            <CircularProgress size="80px"/>
        </div> :
        <div className="edit-user-page">
            <div className="cards">
                <div className="card">
                    <UserForm
                        snackbar={snackbar}
                        userId={user.id}
                        data={data}
                        onChange={setData}
                        showImage={true}
                        showPhone={true}
                        showBirthday={true}
                        requirePhone={true}
                        imageService={userService}
                    />
                    <UserTypeForm
                        data={permissions}
                        onChange={setPermissions}
                    />
                </div>
            </div>
            {showFiles ?
                <>
                    <Divider/>
                    <Cards
                        items={files}
                        paging={paging}
                        dimensions={dimensions}
                        renderer={(file) =>
                            <FileCard
                                key={file.id}
                                document={file}
                                filters={filters}
                            >
                                <IconButton color="secondary" title="Delete" className="delete"
                                            // disabled={strings.isNotBlank(file.templateId)}
                                            onClick={() => {
                                                setFile(file);
                                                setShowDelete(true);
                                            }}>
                                    <DeleteIcon/>
                                </IconButton>
                                {downloading && downloading.id === file.id ?
                                    <CircularProgress size="24px" className="downloading"/> :
                                    <IconButton
                                        color="primary" title="Download"
                                        onClick={() => downloadFile(file)}
                                    >
                                        <DownloadIcon/>
                                    </IconButton>}
                            </FileCard>}
                    />
                </> : null}
            <div className="actions">
                <IconButton color="secondary" title="Previous user"
                            disabled={!prevId}
                            onClick={redirectToPrev}>
                    <PrevIcon/>
                </IconButton>
                <IconButton color="secondary" title="Next user"
                            disabled={!nextId}
                            onClick={redirectToNext}>
                    <NextIcon/>
                </IconButton>
                <VerticalDivider/>
                <Button color="secondary" variant="text"
                        disabled={!modified}
                        onClick={resetForm}>
                    Undo
                </Button>
                <Button color="primary" size="large"
                        disabled={!valid || !modified || !user.id}
                        onClick={submitForm}>
                    Save
                </Button>
            </div>
            {showFiles ?
                <FloatingAddButton
                    title="Add file"
                    position={dimensions.portrait ? "higher" : undefined}
                    center={false}
                    onClick={() => {
                        setShowAdd(true);
                    }}
                /> : null}
            {showAdd ?
                <AddFileDialog
                    snackbar={snackbar}
                    userId={user.id}
                    onCancel={() => {
                        setShowAdd(false);
                    }}
                    onSave={(saved) => {
                        setShowAdd(false);
                        onSaveDocument(saved);
                    }}
                /> : null}
            {showDelete && file ?
                <ConfirmDeleteDialog
                    snackbar={snackbar}
                    title={file.fileName}
                    onCancel={() => {
                        setShowDelete(false);
                        setFile(null);
                    }}
                    onDelete={(fileId) => {
                        setShowDelete(false);
                        setFile(null);
                        onDeleteDocument(fileId);
                    }}
                    deleter={(success, failure) => documentService.deleteFile(file.id, success, failure)}
                /> : null}
            <Prompt when={modified} message={verbiage.UNSAVED_CHANGES}/>
        </div>
}

export default EditUserPage;
